import React, { useEffect, useState } from "react";
import "./Layout.css";
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  Row,
  Col,
  Container,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout } from "../../redux/authentication";
import { FaArrowUp } from "react-icons/fa";

const Layout = ({ children }) => {
  const brandNavy = "#344e66"; // Paupetsoft Brand Navy Theme
  const [showScroll, setShowScroll] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.authentication);

  // LAYOUT DATA IS ARRAY → TAKE FIRST ELEMENT
  const layout = auth?.layoutData?.data?.[0] || {};
  const userRole = auth?.userData?.rights;
  const path = location.pathname;

  // Logout
  const logoutHandler = () => {
    dispatch(handleLogout());
    sessionStorage.clear();
    localStorage.clear();
    navigate("/", { replace: true });
  };

  // Scroll Button Logic
  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Role Check
  const hasAnyRight = (roles = []) => {
    if (!roles?.length) return false;
    return roles.includes(userRole);
  };

  // BRAND TITLE
  const getBrandTitle = () => {
    if (!layout?.brandTitle?.default) return "PAUPET SOFT";

    const special = layout.brandTitle?.specialRoutes || [];
    const matched = special.find((s) => s.routes?.includes(path));
    return matched ? matched.label : layout.brandTitle.default;
  };

  // MENU RENDERING
  const renderMenuItems = () => {
    if (!layout?.menuItems) return null;

    return layout.menuItems.map((menu, index) => {
      // Role check
      if (!hasAnyRight(menu.roles)) return null;

      // SIMPLE MENU ITEM
      if (menu.type === "item") {
        const isActive = path === menu.path;
        return (
          <NavItem key={index}>
            <NavLink
              tag={Link}
              to={menu.path}
              className={isActive ? "active" : ""}
            >
              {menu.label}
            </NavLink>
          </NavItem>
        );
      }

      // DROPDOWN MENU
      return (
        <UncontrolledDropdown nav inNavbar key={index} className="no-print">
          <DropdownToggle nav caret>
            {menu.label}
          </DropdownToggle>

          <DropdownMenu end className="shadow-sm border-0">
            {menu.children?.map((child, cIdx) => {
              if (child.divider) return <DropdownItem divider key={cIdx} />;

              if (child.roles && !hasAnyRight(child.roles)) return null;

              return (
                <DropdownItem
                  key={cIdx}
                  onClick={() => child.path && navigate(child.path)}
                  className="py-2"
                >
                  {child.label}
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </UncontrolledDropdown>
      );
    });
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      {/* NAVBAR WITH LEFT BRAND LOGO */}
      <Navbar
        expand="md"
        style={{ backgroundColor: brandNavy, color: "white" }}
        className="no-print shadow-sm py-2 px-3 sticky-top"
      >
     <NavbarBrand
  tag={Link}
  to="/"
  className="d-flex align-items-center me-auto text-decoration-none text-wrap py-1"
  style={{ maxWidth: "clamp(180px, 60vw, 300px)" }} /* Prevents navbar layout overflow on small screens */
>
  <img
    src="https://paupet-pub.s3.eu-north-1.amazonaws.com/logo512.png"
    alt="Paupet Logo"
    style={{ width: "32px", height: "32px" }}
    className="me-2 flex-shrink-0 align-self-center" /* Stays vertically centered if text spans 2 lines */
  />
  <span
    className="fw-bold text-white text-wrap text-break lh-sm"
    style={{ 
      letterSpacing: "0.03em", 
      fontSize: "clamp(0.95rem, 3.5vw, 1.1rem)" /* Scales down dynamically on phones */
    }}
  >
    {getBrandTitle()}
  </span>
</NavbarBrand>

        <Nav className="ms-auto align-items-center gap-2" navbar>
          {path !== "/" && renderMenuItems()}

          {/* USER INFO */}
          {auth.userData?.user && (
            <NavItem className="text-white opacity-75 small px-2">
              <span>
                User: <strong>{auth.userData?.user}</strong>
              </span>
            </NavItem>
          )}

          {/* LOGOUT BUTTON */}
         {userRole && (
            <NavItem>
              <NavLink
                tag={Link}
                to="#"
                onClick={logoutHandler}
                className="fw-semibold text-warning ms-2"
            >
              Log Out
            </NavLink>
          </NavItem>)
          }
        </Nav>
      </Navbar>

      {/* MAIN CONTENT */}
      <main className="flex-grow-1">{children}</main>

      {/* SCROLL TO TOP BUTTON */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "75px",
            right: "25px",
            zIndex: 1000,
            backgroundColor: brandNavy,
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "42px",
            height: "42px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
          }}
          aria-label="Scroll to top"
        >
          <FaArrowUp />
        </button>
      )}

      {/* FOOTER */}
      <footer
        className="text-center py-2 no-print text-white"
        style={{
          width: "100%",
          backgroundColor: brandNavy,
          fontSize: "11px",
        }}
      >
        <Container>
          <Row>
            <Col>
              <span>
                All rights reserved © {new Date().getFullYear()} PAUPET SOFT
              </span>
              <br />
              <span className="opacity-75">Support: 9886433255</span>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default Layout;